import { Badge, type colors } from "components/Theme/badge";
import UncontrolledTooltip from "components/Theme/tooltip";
import type { SimConnectivityState } from "kubb";
import type React from "react";

/**
 * Traffic light color mapping:
 * - green: PDP context active (fully connected)
 * - amber: Online/Attached but no PDP context
 * - red: Offline
 * - zinc (gray): Unknown or no state
 */
const getColor = (state?: SimConnectivityState | null): keyof typeof colors => {
  if (!state) return "zinc";

  if (state.pdp_context?.active) {
    return "green";
  }

  if (
    state.connection_status === "ONLINE" ||
    state.connection_status === "ATTACHED"
  ) {
    return "amber";
  }

  if (state.connection_status === "OFFLINE") {
    return "red";
  }

  return "zinc";
};

const getLabel = (state?: SimConnectivityState | null): string => {
  if (!state || state.connection_status === "UNKNOWN") return "Unknown";
  if (state.pdp_context?.active) return "Connected";
  if (state.connection_status === "ONLINE") return "Online";
  if (state.connection_status === "ATTACHED") return "Attached";
  return "Offline";
};

const TooltipContent = ({
  state,
}: { state: SimConnectivityState }): React.ReactElement => {
  return (
    <div className="space-y-1 p-1">
      <div>Status: {state.connection_status}</div>
      {state.pdp_context?.active && (
        <>
          <div>
            Technology:{" "}
            {state.pdp_context.rat_type_description ??
              state.pdp_context.rat_type}
          </div>
          {state.pdp_context.ue_ip_address && (
            <div>IP: {state.pdp_context.ue_ip_address}</div>
          )}
        </>
      )}
      {state.network_operator?.operator_name && (
        <div>
          Operator: {state.network_operator.operator_name}
          {state.network_operator.country &&
            ` (${state.network_operator.country})`}
        </div>
      )}
    </div>
  );
};

export default function SimConnectivityStateLabel(
  props: {
    state?: SimConnectivityState | null;
    size?: "sm" | "md" | "lg";
  } & Omit<React.HTMLAttributes<HTMLSpanElement>, "color">,
): React.ReactElement {
  const { size = "lg", state, ...rest } = props;

  return (
    <UncontrolledTooltip
      message={state ? <TooltipContent state={state} /> : "No data received"}
      placement="top"
    >
      <span className={`text-${size}`}>
        <Badge color={getColor(state)} {...rest}>
          {getLabel(state)}
        </Badge>
      </span>
    </UncontrolledTooltip>
  );
}
